import type { AppKey } from './app-detail'
import type { SolutionKey } from '@/lib/solutions'

export type CompareTab = 'without' | 'with'
export type CompareKind = 'stat' | 'quote'

export type CompareCard = {
  key: 'evac' | 'alert' | 'livecount' | 'compliance' | 'quote'
  kind: CompareKind
  /** Brand logo (glyph + wordmark, primary blue) from /apps/minimal, shown
   * top-right. Inverted to white on the blue hero via `iconClass`. */
  logo: string
  /** Accessible name for the logo image, e.g. "Evac". */
  appName: string
  /** Extra classes on the logo, e.g. a white filter on the blue hero. */
  iconClass?: string
  /** Where the stat block sits: 'top' for the mini cards, 'bottom' for the
   * tall hero so its number anchors low like the reference. */
  statAnchor?: 'top' | 'bottom'
  /** Extra classes on the heading / stat number, "with" tab only. */
  titleClass?: string
  statClass?: string
  /** Wrap the bottom chevron in a box: 'light' (secondary box + primary arrow)
   * for pale cards, 'vivid' (#0211D9 box + white arrow) for blue cards. */
  boxedArrow?: 'light' | 'vivid'
  /** Placement inside the 1-hero + 3-stacked grid (lg+). */
  gridClass: string
  /** Background + text classes. Shared by both tabs; the "without" tab lays a
   * grey veil over the top to read as the "before" state. */
  tone: string
  /** Feature page this card links to. */
  link: { category: SolutionKey; feature: AppKey }
}

// ponytail: tones are plain class strings, not a theme system — this is the
// only place they're used. Bump to tokens if a second surface needs them.
const PLAIN = 'border border-border bg-card text-accent-foreground'
const COVER = 'bg-cover bg-center bg-no-repeat'

export const COMPARE_CARDS: readonly CompareCard[] = [
  {
    key: 'compliance',
    kind: 'stat',
    logo: '/apps/minimal/compliassist-logo.webp',
    appName: 'CompliAssist',
    iconClass: 'brightness-0 invert', // render the icon pure white on blue
    statAnchor: 'bottom',
    titleClass: 'text-lg lg:text-xl',
    statClass: 'text-5xl lg:text-7xl',
    boxedArrow: 'vivid',
    gridClass: 'lg:col-span-3 lg:row-span-2',
    // The lone hero carries the 3D texture; the right column stays flat white.
    tone: `text-white bg-primary-vivid bg-[url(/backgrounds/abstract-3d-blue.webp)] ${COVER}`,
    link: { category: 'property', feature: 'compliassist' },
  },
  {
    key: 'evac',
    kind: 'stat',
    logo: '/apps/minimal/evacapp-logo.webp',
    appName: 'Evac',
    statAnchor: 'top',
    statClass: 'text-primary text-4xl lg:text-5xl',
    boxedArrow: 'light',
    gridClass: 'lg:col-start-4 lg:col-span-2 lg:row-start-1',
    tone: PLAIN,
    link: { category: 'people', feature: 'evac' },
  },
  {
    key: 'alert',
    kind: 'stat',
    logo: '/apps/minimal/alertapp-logo.webp',
    appName: 'Alert',
    statAnchor: 'top',
    statClass: 'text-primary text-4xl lg:text-5xl',
    boxedArrow: 'light',
    gridClass: 'lg:col-start-4 lg:col-span-2 lg:row-start-2',
    tone: PLAIN,
    link: { category: 'people', feature: 'alert' },
  },
  {
    key: 'livecount',
    kind: 'stat',
    logo: '/apps/minimal/livecount-logo.webp',
    appName: 'LiveCount',
    statAnchor: 'top',
    statClass: 'text-primary text-4xl lg:text-5xl',
    boxedArrow: 'light',
    gridClass: 'lg:col-start-4 lg:col-span-2 lg:row-start-3',
    tone: PLAIN,
    link: { category: 'people', feature: 'livecount' },
  },
  {
    key: 'quote',
    kind: 'quote',
    logo: '/apps/minimal/safehuman-logo.webp',
    appName: 'SafeHuman',
    // Sits in the left column, under the blue hero.
    gridClass: 'lg:col-start-1 lg:col-span-3 lg:row-start-3',
    tone: 'bg-app-highlight text-primary',
    link: { category: 'people', feature: 'safehuman' },
  },
]
